import { ColDef, PostSortRowsParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useRef } from "react";
import { aggridHeaderClass,  aggridSwitchButtonWrapperStyle } from "src/components/common/common";

import { defaultColDef, getDefaultRowIndexColumnConfig, getRowStyleFlags  } from "src/utils/grid/gridStyleUtils";
import {  formatDateMonth, formatDateYear4 } from "src/utils/string/stringUtil";

import { GridMonthCalendar } from "src/components/common/form/GridMonthCalendar";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { createCalendarCellClickHandler, CreateCellClickHandler, CreateCellDoubleClickHandler, CustomCellClickAction, CustomCellDoubleClickAction, RemoveRowHandler, InsertRowHandler, postSortHandler,  useRowSelectionHandler, getSwitchButtonColumnOptions, createCellCssClassRules, GridCellConfig, createGridDropDownCommitAfter } from "src/utils/grid/GridHandlerContent";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import {  PRD1010_R01Dto } from "./Dto_PRD1010_R01";
import { usePRD1010_R01Store, usePRD1010_R01Store_GridCalendar } from "./Store_PRD1010_R01";
import { usePRD1010_R01CellChange } from "./hooks/PRD1010_R01CellChange";
import { usePRD1010_R01Modal } from "./hooks/usePRD1010_R01Modal";
import { CommonCodeColumn, CompCdColumn,  } from "src/utils/grid/GridColumnContent";
import { GridCustomColumnDefs } from "src/utils/grid/GridCustomColumnDefs";
import { buildDefaultColumnStateFromDefs } from "src/utils/grid/columnPinUtils";
import { cellDateComparator } from 'src/utils/grid/gridFunctionUtil';


const COLUMN_STATE_KEY = 'PRD1010_R01-grid-column-state';

/**
 * 사용자 관리 테이블 컴포넌트
 * 사용자 정보를 표시하고 관리하는 테이블
 */

interface Props {
	clickable: boolean;
	setClickable: (clickable: boolean) => void;
	gridRef: React.RefObject<AgGridReact<PRD1010_R01Dto> | null>;
	defaultPRD1010_R01Row: PRD1010_R01Dto;
}

export const PRD1010_R01Table = (props: Props) => {

	const { 
		clickable, setClickable,
		gridRef,
		defaultPRD1010_R01Row,
	} = props;

    const { 
		PRD1010_R01List, setPRD1010_R01List,
		insertPRD1010_R01List,
		removePRD1010_R01List,
		updatePRD1010_R01List,
		deletedPRD1010_R01List,  addDeletedPRD1010_R01List,
		selectedPRD1010_R01List,  removeSelectedPRD1010_R01List, addSelectedPRD1010_R01List, initSelectedPRD1010_R01List, allSelectedPRD1010_R01List,
		originalPRD1010_R01List,
		columnState, setColumnState,
		scrollTop, setScrollTop,
	} = usePRD1010_R01Store();
	const { compCdList, unitList } = useCommonCodeStore();

	

	// 달력 팝업 노출 여부 상태
	const { PRD1010_R01GridCalendarState, setPRD1010_R01GridCalendarState, initPRD1010_R01GridCalendarState } = usePRD1010_R01Store_GridCalendar();
	const { pickerVisible, pickerPosition, targetRowIndex, targetField, targetDate } = PRD1010_R01GridCalendarState;
	const gridWrapperRef = useRef<HTMLDivElement>(null); // 1. div를 위한 ref 생성

	const { 
		getRowId,
		createRowClassRules,
		createCellClassRules,
		paddedGridData,
		renderIndexCell,
		handleSwitchButtonHandler,
	} = useGridSettingContent({
		gridRef,
		key: 'valueIndex',
		gridData: PRD1010_R01List,
		defaultGridData: defaultPRD1010_R01Row,
		selectedRow: selectedPRD1010_R01List,
		originalGridData: originalPRD1010_R01List,
		updateRow: (index: number, data: any) => updatePRD1010_R01List(index, data),
	});

    const headerNames: Record<string, string> = {
		rowIndex:		"",
		yearMonth:		"생산년월",
		compCd:			"사업장",
		itemCd:			"품목코드",
		itemNm:			"품목명",
		itemSpec:		"규격",
		itemUnit:		"단위",
		planQty:		"생산계획량",
		orderQty:		"작업지시량",
		approveFlg:		"지시승인여부",
		remark:			"비고",
    }

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: ['yearMonth', 'compCd',  'itemNm', 'itemSpec', 'itemUnit'],
		insertedDisabledCells: [''],
		commonDisabledCells: [],
		pointerCells: ['yearMonth', 'itemCd', 'itemNm', 'itemSpec', 'itemUnit', 'planQty', 'orderQty', 'approveYn', 'remark'],
		insertedPointerCells: [''],
		commonPointerCells: [''],
		alignLeftCells: ['yearMonth', 'compCd']
	}), []);

	const {
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<PRD1010_R01Dto>({
			idKey: 'valueIndex',
			selectedList: selectedPRD1010_R01List,
			deletedList: deletedPRD1010_R01List,
			disabledInsertedColumns, disabledColumns,
			pointerInsertedColumns, pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules, }}; 
	}, [
		selectedPRD1010_R01List, deletedPRD1010_R01List,
		disabledInsertedColumns, disabledColumns,
		pointerInsertedColumns, pointerColumns,
		alignLeftColumns,
		createCellClassRules,
	]);

	// 컬럼 클릭 액션 설정, 모달 이 있다면 모달 켜는 함수도 의존성 설정을 해줘야 한다
	const commonInputColumns = [ 'compCd', 'yearMonth', 'itemCd', 'planQty', 'orderQty', 'approveFlg', 'remark', ];

	// 범용 이벤트 훅을 위한 설정
	const defaultEditableColumns = ['compCd', 'yearMonth', 'itemCd', 'planQty', 'orderQty', 'approveFlg', 'remark'];

	const insertRowHandler = useCallback((rowIndex: number, e: React.MouseEvent) => {
		InsertRowHandler({
			event: e,
			dataList: PRD1010_R01List,
			rowData: PRD1010_R01List[rowIndex],
			insertRow: insertPRD1010_R01List,
			defaultRowData: defaultPRD1010_R01Row,
			setClickable
		});
	}, [PRD1010_R01List, insertPRD1010_R01List, defaultPRD1010_R01Row, setClickable]);

	const handleGridDropDownCommitAfter = useMemo(() => 
		createGridDropDownCommitAfter<PRD1010_R01Dto>(defaultEditableColumns),
	[defaultEditableColumns],);

	const baseColumnDefs: ColDef<PRD1010_R01Dto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(
			(params: PRD1010_R01Dto) => renderIndexCell(
				params, 
				(innerParams: PRD1010_R01Dto) => getRowStyleFlags (
					innerParams,
					{
						deletedList: deletedPRD1010_R01List,
						selectedList: selectedPRD1010_R01List,
						dataList: PRD1010_R01List
					}),
				(rowIndex: number, e: React.MouseEvent) => {
					if (e.ctrlKey) {
						for (let i = 0; i < 10; i++) { insertRowHandler(rowIndex, e); }
					} else { 
						insertRowHandler(rowIndex, e);
					}
				},
				(rowData) => RemoveRowHandler( 
					gridRef,
					PRD1010_R01List, 
					rowData,  // rowData는 renderIndexCell에서 Dto 형식으로 선언함
					setClickable, 
					removePRD1010_R01List, 
					(valueIndex) => addDeletedPRD1010_R01List([valueIndex.toString()])
				),
			)
		),
		{
			headerName: '생산년월', colId: 'yearMonth', field: 'yearMonth', width: 90, 
			valueFormatter: (params: any) => {
				if (!params.data?.yearMonth) return '';
				
				const yearMonth = params.data.yearMonth;
				// "202509" 형식인 경우 "2025-09" 형식으로 변환
				if (typeof yearMonth === 'string' && yearMonth.length === 6 && /^\d{6}$/.test(yearMonth)) {
					const year = yearMonth.substring(0, 4);
					const month = yearMonth.substring(4, 6);
					return `${year}-${month}`;
				}
				
				// 다른 형식이면 기존 로직 사용
				return formatDateYear4(new Date(yearMonth)) + '-' + formatDateMonth(new Date(yearMonth));
			},
			comparator: cellDateComparator,
			editable: (params) => params.data?.inserted === true,
		},
		{
			headerName: '사업장', colId: 'compCd', field: 'compCd', width: 100,
			...CompCdColumn.options(compCdList, true, CompCdColumn.change(updatePRD1010_R01List), handleGridDropDownCommitAfter),
			editable: (params) => params.data?.inserted === true,
		},
		{
			headerName: '품목코드', colId: 'itemCd', field: 'itemCd', width: 120,
			editable: (params) => params.data?.inserted === true,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
		},
		{
			headerName: '품목명', colId: 'itemNm', field: 'itemNm', width: 250,
			editable: false
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 250,
			editable: false
		},
		{
			headerName: '단위', colId: 'itemUnit', field: 'itemUnit', width: 100,
			...CommonCodeColumn.options(unitList, false, CommonCodeColumn.change(updatePRD1010_R01List), handleGridDropDownCommitAfter),
		},
		{
			headerName: '생산계획량', colId: 'planQty', field: 'planQty', width: 100, 
			editable: true,
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
		{
			headerName: '작업지시량', colId: 'orderQty', field: 'orderQty', width: 100, 
			editable: true,
			valueFormatter: (params: any) =>
				params.value != null ? params.value.toLocaleString() : '',
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
			valueParser: (params) => Number(String(params.newValue ?? '').replace(/,/g, '').trim()) || 0,
		},
        {
            headerName: '지시승인여부', colId: 'approveFlg', field: 'approveFlg', width: 100,
			...getSwitchButtonColumnOptions('approveFlg', handleSwitchButtonHandler, headerNames, aggridSwitchButtonWrapperStyle, true),
        },
		{
			headerName: '비고', colId: 'remark', field: 'remark',
			editable: true,
			pasteable: true,  // inserted 여부 무관하게 Ctrl+V 허용
		},
		// ...IndexParameterCell(),
	], [deletedPRD1010_R01List, PRD1010_R01List]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, []),
	[baseColumnDefs]);

	const columnDefs: ColDef<PRD1010_R01Dto>[] = useMemo(() => {
		if (columnState === null) {
			return baseColumnDefs;
		}
		return GridCustomColumnDefs<PRD1010_R01Dto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [baseColumnDefs, columnState, headerNames]);

    
	
	const { settingCodeSearchModalProps } = usePRD1010_R01Modal({
		PRD1010_R01List,
		updatePRD1010_R01List,
		originalPRD1010_R01List,
		gridRef,
	});

	const { handleCellChange } = usePRD1010_R01CellChange({
		updatePRD1010_R01List,
		gridRef,
		settingCodeSearchModalProps,
	});


    const handleRowClick = useRowSelectionHandler({
        dataList: PRD1010_R01List,
        idKey: 'valueIndex',
        selectedList: selectedPRD1010_R01List,
        deletedList: deletedPRD1010_R01List,
        addSelected: addSelectedPRD1010_R01List,
        removeSelected: removeSelectedPRD1010_R01List,
        clickable, setClickable
    });

	// 범용 이벤트 훅 사용 (코드 검색 없이) - 방향키 이동 기능 유지
	/**
	 * 기본 상세 테이블 이벤트 핸들러 훅
	 * 
	 * 주요 기능:
	 * - 키다운 네비게이션 (방향키 이동)
	 * - 셀 클릭/키다운 이벤트 처리
	 * 
	 * @param detailList - 상세 데이터 목록
	 * @param editableColumns - 편집 가능한 컬럼 목록
	 */
	const cellEventHandlers = useDetailTableEvents({
		detailList: PRD1010_R01List as any,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		}
	});

	const cellClickActions: CustomCellClickAction<PRD1010_R01Dto>[] = useMemo(() => [
		{
			validate: (field, data) => field === 'yearMonth',
			action: createCalendarCellClickHandler('yearMonth', PRD1010_R01GridCalendarState, setPRD1010_R01GridCalendarState),
		},
		{
			validate: (field, data) => commonInputColumns.includes(field),
			//aggrid 기본 편집 기능
			action: (e) => { e.api.startEditingCell({ rowIndex: e.rowIndex!, colKey: e.colDef.field! }); }
		},
	], [PRD1010_R01GridCalendarState]);
	const handleCellClick = CreateCellClickHandler(PRD1010_R01List, 'valueIndex', cellClickActions);
	
	//더블클릭 액션 없음
	const cellDoubleClickActions: CustomCellDoubleClickAction<PRD1010_R01Dto>[] = useMemo(() => [
		{
			validate: (field, data) => (field === 'itemCd' || field === 'itemNm' || field === 'itemSpec' || field === 'itemUnit'),
			action: (e, foundData) => {
				settingCodeSearchModalProps('품목', foundData.itemCd!, false, 'itemCd', foundData, foundData.valueIndex!);
			}
		},
	], [settingCodeSearchModalProps]);
	const handleCellDoubleClick = CreateCellDoubleClickHandler(PRD1010_R01List, 'valueIndex', cellDoubleClickActions);

	const handlePostSort = useCallback((params: PostSortRowsParams<PRD1010_R01Dto>) => {
		postSortHandler(params, PRD1010_R01List, originalPRD1010_R01List, setPRD1010_R01List);
	}, [PRD1010_R01List, originalPRD1010_R01List, ]);

	const handleCalendarChange = useCallback((date: Date | undefined, rowIndex: number, targetField: string) => {
		if (rowIndex !== null && targetField) {
			const foundData = PRD1010_R01List[rowIndex];
			if (foundData) {
				updatePRD1010_R01List(foundData.valueIndex!, {
					...foundData,
					[targetField]: date ? formatDateYear4(date) + formatDateMonth(date) : '',
					isModified: true // 날짜 변경 시 수정 상태로 설정
				});
			}
		}
	}, [PRD1010_R01List, updatePRD1010_R01List]);

	const handleCalendarLoad = useCallback((rowIndex: number, targetField: string) => {
		const foundData = PRD1010_R01List[rowIndex];
		if (!foundData) return;

		const dateString = foundData[targetField as keyof PRD1010_R01Dto] as string;
		
		if(dateString && typeof dateString === 'string' && dateString.length === 6 && /^\d{6}$/.test(dateString)) {
			// "YYYYMM" 형식인 경우 올바른 Date 객체로 변환
			const year = parseInt(dateString.substring(0, 4), 10);
			const month = parseInt(dateString.substring(4, 6), 10) - 1; // JavaScript Date는 월이 0부터 시작
			setPRD1010_R01GridCalendarState({ targetDate: new Date(year, month, 1) });
		} else {
			// 값이 없거나 형식이 다르면 오늘 날짜 기준으로 설정
			setPRD1010_R01GridCalendarState({ targetDate: new Date() });
		}
	}, [PRD1010_R01List, setPRD1010_R01GridCalendarState]);

	const pinnedBottomRow = useMemo(() => {
		const totalPlanQty = PRD1010_R01List.reduce((sum, item) => sum + Number(item.planQty) || 0, 0);
		const totalOrderQty = PRD1010_R01List.reduce((sum, item) => sum + Number(item.orderQty) || 0, 0);
		return [{
			rowIndex: '계',
			planQty: totalPlanQty.toLocaleString(),
			orderQty: totalOrderQty.toLocaleString(),
			editable: false,
		}];
	},[PRD1010_R01List, deletedPRD1010_R01List]);

    return (
		<GridContextWrapper<PRD1010_R01Dto>
			enableCellCalculation
			gridWrapperRef={gridWrapperRef}
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			setPaddedGridData={setPRD1010_R01List}
			initSelected={initSelectedPRD1010_R01List}
			defaultRowData={defaultPRD1010_R01Row}
			allSelected={allSelectedPRD1010_R01List}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Excel<PRD1010_R01Dto>
				ref={gridRef}
				rowData={paddedGridData}	getRowId={getRowId} // +- 버튼 테이블 getRowId는 valueIndex로 고정
				columnDefs={columnDefs}		
				defaultColDef={cellCssDefaultColDef}

				rowClassRules={createRowClassRules<PRD1010_R01Dto>({
					idKey: 'valueIndex',
					selectedList: selectedPRD1010_R01List,
					deletedList: deletedPRD1010_R01List
				})}
				onRowClicked={handleRowClick}
				onCellClicked={handleCellClick}
				onCellDoubleClicked={handleCellDoubleClick}
				onCellKeyDown={cellEventHandlers.handleCellKeyDown}
				onCellValueChanged={handleCellChange}
				pinnedBottomRowData={pinnedBottomRow}

				onPostSortRows={handlePostSort} //onSortChanged 삭제, 커스텀 정렬 기능추가 
                // Grid_Select에 공통 로직 처리를 위한 props 전달
                columnStateKey={COLUMN_STATE_KEY}			//컬럼 상태 키(로컬스토리지)	
				setColumnState={setColumnState}				//컬럼 상태 저장
				getDefaultColumnState={() => defaultColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
				
			/>
			<GridMonthCalendar
				rowIndex={targetRowIndex!}
				targetField={targetField!}
				visible={pickerVisible}
				setVisible={(visible) => setPRD1010_R01GridCalendarState({ pickerVisible: visible })}
				value={targetDate}
				pickerPosition={pickerPosition}
				onClear={() => {
					if (targetRowIndex !== null && targetField) {
						handleCalendarChange(undefined, targetRowIndex, targetField);
					}
				}}
				onChange={handleCalendarChange}
				onClickLoad={handleCalendarLoad}
			/>
		</GridContextWrapper>
    )
}
